import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Chip,
  Grid,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  TablePagination,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Info as InfoIcon,
  Clear as ClearIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useGetAuditLogsQuery, useGetAuditLogActionsQuery } from '../services/auditLog.service';
import { useGetUsersQuery } from '../services/users.service';
import { useGetLocationsQuery } from '../services/locations.service';

const AuditLogsPage: React.FC = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [filters, setFilters] = useState({
    userId: '',
    entity: '',
    action: '',
    locationId: '',
    startDate: null as Date | null,
    endDate: null as Date | null,
  });
  const [detailsDialog, setDetailsDialog] = useState<any>(null);

  // Fetch filter options
  const { data: actionsData } = useGetAuditLogActionsQuery();
  const { data: usersData } = useGetUsersQuery({ limit: 100 });
  const { data: locationsData } = useGetLocationsQuery({ active: true });

  // Fetch audit logs
  const {
    data: logsResponse,
    isLoading,
    error,
    refetch
  } = useGetAuditLogsQuery({
    ...filters,
    startDate: filters.startDate ? filters.startDate.toISOString() : undefined,
    endDate: filters.endDate ? filters.endDate.toISOString() : undefined,
    page: page + 1,
    limit: rowsPerPage
  });

  const logs = logsResponse?.data || [];
  const pagination = logsResponse?.pagination;

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleClearFilters = () => {
    setFilters({
      userId: '',
      entity: '',
      action: '',
      locationId: '',
      startDate: null,
      endDate: null,
    });
    setPage(0);
  };

  const getActionColor = (action: string): 'default' | 'success' | 'warning' | 'error' | 'info' => {
    if (action.includes('CREATE')) return 'success';
    if (action.includes('UPDATE')) return 'info';
    if (action.includes('DELETE')) return 'error';
    if (action.includes('LOGIN')) return 'default';
    if (action.includes('FAILED')) return 'error';
    return 'default';
  };

  const getEntityIcon = (entity: string) => {
    switch (entity) {
      case 'Auth': return '🔐';
      case 'Product': return '📦';
      case 'Sale': return '💰';
      case 'Inventory': return '📊';
      case 'User': return '👤';
      case 'CashDrawer': return '💵';
      case 'Transfer': return '🔄';
      default: return '📝';
    }
  };

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Error al cargar los logs de auditoría
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          Logs de Auditoría
        </Typography>
        <Box>
          <Tooltip title="Actualizar">
            <IconButton onClick={() => refetch()}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Usuario</InputLabel>
              <Select
                value={filters.userId}
                onChange={(e) => setFilters({ ...filters, userId: e.target.value })}
                label="Usuario"
              >
                <MenuItem value="">Todos</MenuItem>
                {usersData?.users?.map((user: any) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, md: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Entidad</InputLabel>
              <Select
                value={filters.entity}
                onChange={(e) => setFilters({ ...filters, entity: e.target.value })}
                label="Entidad"
              >
                <MenuItem value="">Todas</MenuItem>
                {actionsData?.data?.entities?.map((entity) => (
                  <MenuItem key={entity.value} value={entity.value}>
                    {entity.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, md: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Acción</InputLabel>
              <Select
                value={filters.action}
                onChange={(e) => setFilters({ ...filters, action: e.target.value })}
                label="Acción"
              >
                <MenuItem value="">Todas</MenuItem>
                {actionsData?.data?.actions?.map((action) => (
                  <MenuItem key={action.value} value={action.value}>
                    {action.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, md: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Ubicación</InputLabel>
              <Select
                value={filters.locationId}
                onChange={(e) => setFilters({ ...filters, locationId: e.target.value })}
                label="Ubicación"
              >
                <MenuItem value="">Todas</MenuItem>
                {locationsData?.data?.map((location) => (
                  <MenuItem key={location.id} value={location.id}>
                    {location.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, md: 2 }}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
              <DatePicker
                label="Fecha Inicio"
                value={filters.startDate}
                onChange={(newValue) => setFilters({ ...filters, startDate: newValue })}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>

          <Grid size={{ xs: 12, md: 2 }}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
              <DatePicker
                label="Fecha Fin"
                value={filters.endDate}
                onChange={(newValue) => setFilters({ ...filters, endDate: newValue })}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>

          <Grid size={{ xs: 12, md: 2 }}>
            <Tooltip title="Limpiar filtros">
              <Button
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={handleClearFilters}
                fullWidth
                size="small"
              >
                Limpiar
              </Button>
            </Tooltip>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper} sx={{ flexGrow: 1, overflow: 'auto' }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Fecha/Hora</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Usuario</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Entidad</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Acción</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Ubicación</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Detalles</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Info</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.map((log) => (
                <TableRow key={log.id} hover>
                  <TableCell>
                    <Typography variant="body2">
                      {format(new Date(log.createdAt), 'dd/MM/yyyy HH:mm:ss', { locale: es })}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PersonIcon fontSize="small" color="action" />
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {log.user.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {log.user.role}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={`${getEntityIcon(log.entity)} ${log.entity}`}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={actionsData?.data?.actions?.find(a => a.value === log.action)?.label || log.action}
                      size="small"
                      color={getActionColor(log.action)}
                    />
                  </TableCell>
                  <TableCell>
                    {log.location ? (
                      <Typography variant="body2">{log.location.name}</Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {log.details ? (
                      <Typography variant="body2" sx={{ 
                        maxWidth: 200, 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap' 
                      }}>
                        {typeof log.details === 'object' ? 
                          Object.entries(log.details)
                            .filter(([key]) => key !== 'locationId' && key !== 'userId')
                            .map(([key, value]) => `${key}: ${value}`)
                            .join(', ')
                          : log.details
                        }
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Ver detalles completos">
                      <IconButton
                        size="small"
                        onClick={() => setDetailsDialog(log)}
                      >
                        <InfoIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {pagination && (
        <TablePagination
          component="div"
          count={pagination.total}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Filas por página:"
        />
      )}

      {/* Details Dialog */}
      <Dialog
        open={!!detailsDialog}
        onClose={() => setDetailsDialog(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Detalles del Log
        </DialogTitle>
        <DialogContent dividers>
          {detailsDialog && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">ID:</Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>{detailsDialog.id}</Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">Fecha/Hora:</Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    {format(new Date(detailsDialog.createdAt), 'dd/MM/yyyy HH:mm:ss', { locale: es })}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">Usuario:</Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    {detailsDialog.user.name} ({detailsDialog.user.email})
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">Rol:</Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>{detailsDialog.user.role}</Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">Entidad:</Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>{detailsDialog.entity}</Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">Acción:</Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>{detailsDialog.action}</Typography>
                </Grid>
                {detailsDialog.entityId && (
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="subtitle2" color="text.secondary">ID de Entidad:</Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>{detailsDialog.entityId}</Typography>
                  </Grid>
                )}
                {detailsDialog.location && (
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="subtitle2" color="text.secondary">Ubicación:</Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>{detailsDialog.location.name}</Typography>
                  </Grid>
                )}
                {detailsDialog.details && (
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="subtitle2" color="text.secondary">Detalles:</Typography>
                    <Paper sx={{ p: 2, mt: 1, bgcolor: 'grey.100' }}>
                      <pre style={{ margin: 0, fontSize: '0.875rem', overflow: 'auto' }}>
                        {JSON.stringify(detailsDialog.details, null, 2)}
                      </pre>
                    </Paper>
                  </Grid>
                )}
                {detailsDialog.metadata && (
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="subtitle2" color="text.secondary">Metadata:</Typography>
                    <Paper sx={{ p: 2, mt: 1, bgcolor: 'grey.100' }}>
                      <pre style={{ margin: 0, fontSize: '0.875rem', overflow: 'auto' }}>
                        {JSON.stringify(detailsDialog.metadata, null, 2)}
                      </pre>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialog(null)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AuditLogsPage;